import React, { useEffect, useMemo, useState } from 'react';
import {
  Bed,
  Beer,
  Cake,
  Calendar,
  Car,
  Coffee,
  Heart,
  Music,
  Plane,
  Repeat,
  Users,
  Utensils,
  Wine,
  Zap,
  Bolt,
  Smartphone,
  ArrowRight,
  ShieldCheck,
} from 'lucide-react';
import { CleroButtonWordmark } from './Logo';
import CompareShell, { AppStoreBadges, InstitutionalTrustBar } from './compare/CompareShell';
import { firebaseProjectId, isUsingFirebaseEmulator } from '../services/firebase';

type Props = {
  token: string;
};

type InviteResolution =
  | { state: 'loading' }
  | { state: 'valid'; groupId: string; groupName: string; settlementType?: string; color?: string; icon?: string; photoURL?: string }
  | { state: 'not_found'; reason: 'not_found' | 'deleted' | 'disabled' };

const iconMap: Record<string, React.ComponentType<{ size?: number; color?: string; fill?: string; strokeWidth?: number }>> = {
  users: Users,
  utensils: Utensils,
  coffee: Coffee,
  beer: Beer,
  wine: Wine,
  plane: Plane,
  car: Car,
  bed: Bed,
  heart: Heart,
  music: Music,
  cake: Cake,
  zap: Zap,
};

const normalizeGroupIconKey = (value: unknown): string => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const canonical = raw
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase();

  const aliases: Record<string, string> = {
    // User aliases
    'user-group': 'users',
    user_group: 'users',
    usergroup: 'users',
    'users-group': 'users',
    'users-round': 'users',
    roommates: 'users',
    friends: 'users',

    // Bills & Utilities
    'credit-card': 'zap',
    creditcard: 'zap',
    credit_card: 'zap',
    bills: 'zap',
    utilities: 'zap',
    briefcase: 'zap',
    'briefcase-business': 'zap',
    briefcasebusiness: 'zap',
    work: 'zap',
    bolt: 'zap',

    // Stay & Living
    house: 'bed',
    rent: 'bed',
    home: 'bed',
    stay: 'bed',
    hotel: 'bed',

    // Rides & Travel
    carfront: 'car',
    'car-front': 'car',
    ride: 'car',
    roadtrip: 'car',
    trip: 'plane',
    travel: 'plane',
    flight: 'plane',

    // Food & Groceries
    'shopping-bag': 'utensils',
    shoppingbag: 'utensils',
    shopping_bag: 'utensils',
    groceries: 'utensils',
    dinner: 'utensils',
    food: 'utensils',
    meal: 'utensils',

    // Drinks
    drinks: 'beer',
    cocktail: 'wine',

    // Celebrations
    party: 'cake',
    partypopper: 'cake',
    'party-popper': 'cake',
    birthday: 'cake',
    gift: 'cake',
    gifts: 'cake',

    // Entertainment
    film: 'music',
    movie: 'music',
    movies: 'music',
    cinema: 'music',
    events: 'music',
    concerts: 'music',

    // Family
    family: 'heart',
  };

  return aliases[canonical] || aliases[raw] || canonical;
};

const fallbackBubbleColor = (seed: string) => {
  const normalized = String(seed || '').trim();
  let hash = 0;
  for (let i = 0; i < normalized.length; i += 1) {
    hash = normalized.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = ['#0D9488', '#0284C7', '#6366F1', '#8B5CF6', '#EC4899', '#F43F5E', '#D97706', '#059669'];
  return colors[Math.abs(hash) % colors.length] || '#0D9488';
};

const isPhotoUrl = (value: unknown): boolean => {
  const str = String(value || '').trim();
  return str.startsWith('http://') || str.startsWith('https://') || str.startsWith('data:');
};

const ResolvedGroupArtwork: React.FC<{
  iconValue: unknown;
  settlementType?: string;
  photoURL?: string;
}> = ({ iconValue, settlementType, photoURL }) => {
  const effectivePhotoUrl = photoURL || (isPhotoUrl(iconValue) ? String(iconValue) : null);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [effectivePhotoUrl]);

  if (effectivePhotoUrl && !imgFailed) {
    return (
      <img
        src={effectivePhotoUrl}
        alt=""
        className="h-full w-full object-cover rounded-2xl"
        onError={() => setImgFailed(true)}
      />
    );
  }

  const normalizedKey = normalizeGroupIconKey(iconValue);
  const Icon = iconMap[normalizedKey];
  if (Icon) {
    return <Icon size={36} color="#FFFFFF" fill="#FFFFFF" strokeWidth={1.5} />;
  }

  if (settlementType === 'monthly') {
    return <Repeat size={36} color="#FFFFFF" strokeWidth={2.4} />;
  }
  if (settlementType === 'on_date') {
    return <Calendar size={36} color="#FFFFFF" strokeWidth={2.4} />;
  }
  return <Bolt size={36} color="#FFFFFF" strokeWidth={2.4} />;
};

const getResolveGroupInviteLinkUrl = (token: string) => {
  const encodedToken = encodeURIComponent(token);
  if (isUsingFirebaseEmulator) {
    return `http://${window.location.hostname}:5001/${firebaseProjectId}/us-central1/resolveGroupInviteLink?token=${encodedToken}`;
  }
  return `https://us-central1-${firebaseProjectId}.cloudfunctions.net/resolveGroupInviteLink?token=${encodedToken}`;
};

const GroupInvitePage: React.FC<Props> = ({ token }) => {
  const [resolution, setResolution] = useState<InviteResolution>({ state: 'loading' });

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const response = await fetch(getResolveGroupInviteLinkUrl(token), {
          method: 'GET',
          headers: { Accept: 'application/json' },
        });
        const data = await response.json();
        if (cancelled) return;
        if (data?.ok) {
          setResolution({
            state: 'valid',
            groupId: String(data.groupId || ''),
            groupName: String(data.groupName || 'Group'),
            settlementType: data?.settlementType ? String(data.settlementType) : undefined,
            color: data?.color ? String(data.color) : undefined,
            icon: data?.icon ? String(data.icon) : undefined,
            photoURL: data?.photoURL ? String(data.photoURL) : undefined,
          });
          return;
        }
        setResolution({ state: 'not_found', reason: (data?.reason || 'not_found') as 'not_found' | 'deleted' | 'disabled' });
      } catch {
        if (!cancelled) setResolution({ state: 'not_found', reason: 'not_found' });
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const deepLink = useMemo(() => `clero://group-invite/${encodeURIComponent(token)}`, [token]);

  const openInClero = () => {
    window.location.href = deepLink;
  };

  const groupColor = resolution.state === 'valid'
    ? (resolution.color || fallbackBubbleColor(resolution.groupId || resolution.groupName))
    : '#0D9488';

  return (
    <CompareShell>
      {/* HERO SECTION WITH ATMOSPHERIC AMBIENT MESH */}
      <section className="relative overflow-hidden pt-8 sm:pt-14 pb-14 sm:pb-20 px-4 sm:px-6">
        {/* Soft Ambient Radial Lights (Faithful to Landing Page public-hero-mesh) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[500px] w-full max-w-5xl rounded-full bg-[radial-gradient(circle_at_50%_20%,rgba(13,148,136,0.12),rgba(98,199,255,0.06)_40%,transparent_70%)] blur-3xl -z-10"
        />

        <div className="relative mx-auto max-w-2xl rounded-[32px] sm:rounded-[40px] bg-white/95 backdrop-blur-2xl p-8 sm:p-12 shadow-[0_28px_80px_-16px_rgba(15,23,42,0.10),0_1px_2px_rgba(15,23,42,0.04)] border border-slate-200/80 ring-1 ring-white/60 text-center space-y-6">
          {/* Group Icon Avatar */}
          {resolution.state === 'valid' ? (
            <div
              className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl shadow-md ring-4 ring-slate-100/90 transition-transform hover:scale-105 overflow-hidden"
              style={{ backgroundColor: (resolution.photoURL || isPhotoUrl(resolution.icon)) ? 'transparent' : groupColor }}
            >
              <ResolvedGroupArtwork
                iconValue={resolution.icon}
                settlementType={resolution.settlementType}
                photoURL={resolution.photoURL}
              />
            </div>
          ) : resolution.state === 'loading' ? (
            <div className="mx-auto h-20 w-20 animate-pulse rounded-2xl bg-slate-100" />
          ) : null}

          {/* Heading & Subtitle */}
          {resolution.state === 'loading' ? (
            <div className="space-y-3 max-w-sm mx-auto">
              <div className="h-10 w-3/4 animate-pulse rounded-2xl bg-slate-100 mx-auto" />
              <div className="h-5 w-full animate-pulse rounded-xl bg-slate-100" />
            </div>
          ) : resolution.state === 'valid' ? (
            <div className="space-y-3">
              <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-slate-950 leading-tight">
                Join <span className="text-teal-600">{resolution.groupName}</span> on Clero.
              </h1>
              <p className="mx-auto max-w-lg text-base sm:text-lg text-slate-600 font-medium leading-relaxed">
                Open the app to join the group, view shared balances, and auto-settle on your terms with $0 transfer fees.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <h1 className="text-2xl sm:text-4xl font-black tracking-tight text-slate-950">
                Group link unavailable
              </h1>
              <p className="mx-auto max-w-lg text-base text-slate-600 font-medium leading-relaxed">
                This group may have been deleted or the link is disabled. You can download Clero below to start a new group.
              </p>
            </div>
          )}

          {/* Primary Action Button (Clean, no overlapping arrow icon) */}
          {resolution.state === 'valid' && (
            <div className="pt-2">
              <button
                type="button"
                onClick={openInClero}
                className="inline-flex items-center justify-center rounded-2xl bg-[var(--clero-primary)] hover:bg-[var(--clero-primary-dark)] text-white font-extrabold text-base sm:text-lg px-8 py-4 shadow-[0_10px_25px_-5px_rgba(13,148,136,0.38)] hover:shadow-[0_16px_32px_-6px_rgba(13,148,136,0.48)] hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all duration-200 w-full sm:w-auto cursor-pointer"
              >
                <span className="whitespace-nowrap flex items-center gap-2">
                  <span>Open in</span>
                  <CleroButtonWordmark height={17} color="#FFFFFF" className="inline-block shrink-0" />
                </span>
              </button>
            </div>
          )}

          {/* Download App Badges */}
          <div className="pt-2 flex items-center justify-center">
            <AppStoreBadges badgeHeightClass="h-10 sm:h-11" />
          </div>
        </div>
      </section>

      {/* INSTITUTIONAL BANKING INFRASTRUCTURE & SECURITY METRICS BAR */}
      <InstitutionalTrustBar />

      {/* HOW CLERO GROUPS WORK (FLIGHTY STYLE) */}
      <section className="py-8 px-4 sm:px-6">
        <div className="mx-auto max-w-5xl rounded-3xl bg-white p-8 sm:p-12 space-y-8">
          <div className="border-b border-slate-100 pb-6 text-center sm:text-left">
            <h2 className="text-2xl sm:text-3xl font-black text-slate-950 tracking-tight">
              Real money movement for shared group living
            </h2>
            <p className="mt-2 text-base text-slate-600 font-medium leading-relaxed">
              Clero eliminates the friction of group finances by combining shared expense ledgers with automated bank payments.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            <div className="space-y-2.5">
              <h3 className="text-base font-bold text-slate-950">1. Purpose-built group cycles</h3>
              <p className="text-sm text-slate-600 leading-relaxed font-medium">
                Choose <strong>Monthly</strong> roommate mode that auto-settles on the 1st, <strong>Trip</strong> mode for vacations that auto-closes on your return, or <strong>On-Demand</strong> running tabs.
              </p>
            </div>

            <div className="space-y-2.5">
              <h3 className="text-base font-bold text-slate-950">2. Free AI receipt splitting</h3>
              <p className="text-sm text-slate-600 leading-relaxed font-medium">
                Scan grocery and restaurant receipts with AI or sync email charges. Clero splits tax and tip proportionally to the exact cent without subscriptions.
              </p>
            </div>

            <div className="space-y-2.5">
              <h3 className="text-base font-bold text-slate-950">3. Direct checking settlement</h3>
              <p className="text-sm text-slate-600 leading-relaxed font-medium">
                Payments move straight from bank account to bank account on the same day* for $0. No app wallet middleman, no instant cashout fees.
              </p>
            </div>
          </div>
        </div>
      </section>
    </CompareShell>
  );
};

export default GroupInvitePage;
